// ゲームダッシュボード（DOMポータル）。`/` の入口。
// モック docs/mockups/dashboard-mock.html をベースに、実データ（プレイヤー/ランキング）と
// 「遊ぶ」起動コールバックを接続したもの。ライトで親しみやすいカジュアルゲームポータル。
import {
  getNickname,
  getCountry,
  getPlayerId,
  setPlayerId,
  setNickname,
  setCountry,
  getLanguage,
  setLanguage,
  hasLocalPlayerData,
} from '@/platform/net/playerId';
import { isPasskeySupported, loginWithPasskey, registerPasskey } from '@/platform/net/passkey';
import { backfillScores, collectLocalRecords, hasContributions } from '@/platform/net/merge';
import { loadLanguage, t } from '@/platform/i18n';
import { pushSettingsToServer, reconcileSettingsWithServer } from '@/platform/net/settings';
import {
  registerPlayer,
  fetchGlobalLeaderboard,
  fetchCountryLeaderboard,
  fetchMedalLeaderboard,
  fetchBalanceLeaderboard,
  fetchPopularGames,
} from '@/platform/net/leaderboard';
// 登録簿だけを見る軽いモジュール（設定スライスとカード情報のみ。ゲーム本体＝Phaser は落ちてこない）。
import { GAMES, UPCOMING, DEFAULT_GAME } from '@/games/registry';
import type { CoverTile, GameEntry, UpcomingEntry } from '@/games/registry';
import type {
  GlobalLeaderboardEntry,
  CountryLeaderboardEntry,
  MedalLeaderboardEntry,
  BalanceLeaderboardEntry,
  PopularGameEntry,
} from '@/platform/types/leaderboard';
import { sharePercents } from './playShare';
import { flagEmoji, countryName } from './countries';
import { promptRegister } from './registerModal';
import { legalAnchor } from './legalLinks';
import { confirmDeleteData } from './deleteDataModal';
import { confirmLogout } from './logoutModal';
import { openSettingsModal } from './settingsModal';
import { openDevices } from './devicesModal';
import { fetchSessionState, logoutThisDevice, shouldOfferResume } from '@/platform/net/account';
import { confirmMerge } from './mergeModal';
import { promptTransferCode, showTransferCode } from './transferModal';
import { setPortalTitle } from './documentTitle';
// ⚠️ 文字列定数だけの軽いモジュール（ポーカー本体 `pokerScreen.ts` は動的 import のまま）。
import { POKER_LOGO, POKER_LOGO_H, POKER_LOGO_W } from '@/platform/poker/images';
import {
  CHINCHIRO_LOGO,
  CHINCHIRO_LOGO_H,
  CHINCHIRO_LOGO_W,
} from '@/platform/chinchiro/images';
import type { LoggedInPlayer } from '@/platform/types/session';

export interface DashboardOptions {
  /** ゲーム起動。gameKey は `GAMES` の key ＝ カードの `data-play` と同じ値。 */
  onPlay: (gameKey: string) => void;
}

/** ダッシュボードを root に描画（再描画も同関数を再呼び出し）。 */
export function renderDashboard(root: HTMLElement, opts: DashboardOptions): void {
  injectCss();
  const nickname = getNickname();
  const country = getCountry();
  const registered = !!nickname && nickname.trim().length > 0;

  // プレイヤー名の入力/編集はダッシュボードに一元化。ここでタブタイトルへ反映する
  // （初回描画・登録/編集後の再描画いずれもここを通る）。
  // **ゲームから戻ったときもここを通る**ので、ゲーム名のタイトルがポータル名へ戻る。
  setPortalTitle(nickname);

  root.innerHTML = markup(nickname, country, registered);

  const reRender = () => renderDashboard(root, opts);

  const openRegister = async () => {
    const res = await promptRegister({
      nickname: nickname ?? '',
      country: country ?? 'JP',
      lang: getLanguage(),
    });
    if (!res) return;
    setNickname(res.nickname);
    setCountry(res.country);
    // 表示言語が変わったらカタログを読み直す（再描画で新しい言語が出る）。
    // ゲーム側のカタログは `enterGame` が遊ぶ直前に読むので、ここではポータルだけでよい。
    const langChanged = res.lang !== getLanguage();
    setLanguage(res.lang);
    if (langChanged) await loadLanguage(getLanguage(), ['portal']);
    let registeredOnServer = true;
    try {
      await registerPlayer(res.nickname, res.country, res.lang);
    } catch {
      // オフラインでもローカル登録は保持。次回送信時に反映される。
      // ⚠️ サーバーに行が無いまま進捗や設定を送ると外部キー違反（500）になるので、
      //    失敗したらこの先の同期は諦める（次回の起動で追いつく）。
      registeredOnServer = false;
    }
    if (registeredOnServer) {
      // 🔴 **登録は「この端末の記録をアカウントへ持ち込む」でもある**（2026-08-08 の
      //    本番実機テストで発覚）。以前は設定同期しか呼んでおらず、進捗も★も置き去りだった。
      //    2つを別々に送るのは、両者の**書かれるきっかけが元から別**だから:
      //      - 進捗 … `reconcileProgressWithServer` の初回シード（新規アカウントは空なので吸い上がる）
      //      - ★   … `scores` は「クリアした瞬間」にしか書かれないので送り直しが要る
      //    どちらも冪等なので、プロフィール編集で再実行されても壊れない。
      await adoptLocalRecords(false);
    }
    reRender();
  };

  // イベント配線
  root.querySelectorAll<HTMLElement>('[data-play]').forEach((btn) => {
    btn.addEventListener('click', () => opts.onPlay(btn.dataset.play || DEFAULT_GAME.key));
  });
  root.querySelectorAll<HTMLElement>('[data-register]').forEach((btn) => {
    btn.addEventListener('click', openRegister);
  });
  // 音の設定（2026-09-20・実機の気づいたこと2「トップ画面に設定ボタン配置」）。
  // ⚠️ ここは**描き直さない**——音量と BGM はこの画面に何も表示していないので、
  //    閉じたあとに見た目が変わるところが無い。
  root.querySelectorAll<HTMLElement>('[data-settings]').forEach((btn) => {
    btn.addEventListener('click', () => void openSettingsModal());
  });
  root.querySelectorAll<HTMLElement>('[data-passkey]').forEach((btn) => {
    btn.addEventListener('click', () => {
      // 生体認証のシートが出ている間に二度押しされないよう、その場で無効化する。
      const label = btn.textContent;
      btn.setAttribute('disabled', '');
      btn.textContent = t('portal.passkey.working');
      void registerPasskey(getPlayerId()).then((res) => {
        btn.removeAttribute('disabled');
        btn.textContent = label;
        // 「やめた」は失敗ではないので何も出さない（驚かせるだけ）。
        if (!res.ok && res.reason === 'cancelled') return;
        flash(
          root,
          res.ok
            ? t('portal.passkey.done', { device: res.label ?? '' })
            : // 「この端末は登録済み」を失敗と同じ文言にしない。再試行しても永久に成功しない。
              res.reason === 'already'
              ? t('portal.passkey.already')
              : t('portal.passkey.failed'),
        );
      });
    });
  });
  /**
   * ログインが通ったあとの共通処理。**入口は2つある**（パスキー / 引き継ぎコード）が、
   * ここから先は完全に同じなので1本にまとめる。
   *
   * 🔴 **持ち主を差し替える前に、この端末の記録の行き先を決める**。
   *    ログイン成功でセッションは発行済みなので、統合 API はもう呼べる。
   *    逆にここで先に `setPlayerId` してしまうと、統合をやめたときに
   *    「他人のIDを名乗ったまま自分の記録を持っている」状態になる。
   *
   * @returns 取り込んだら true / 統合をやめて中止したら false
   */
  const finishLogin = async (
    player: LoggedInPlayer,
    doneKey: string,
    doneMergedKey: string,
  ): Promise<boolean> => {
    const games = await collectLocalRecords();
    let merged = false;
    if (hasContributions(games)) {
      // やめた／失敗した場合は**ログインごと中止**する。統合せずに続けると、
      // このあとの `reconcileProgress` がサーバーの進捗でローカルを置き換え、
      // この端末の記録が黙って消える。
      if (!(await confirmMerge(games))) return false;
      merged = true;
    }

    // ここで初めてこの端末の持ち主が入れ替わる。
    setPlayerId(player.id);
    setNickname(player.nickname);
    if (player.country) setCountry(player.country);
    const langChanged = !!player.lang && player.lang !== getLanguage();
    if (player.lang) setLanguage(player.lang);
    if (langChanged) await loadLanguage(getLanguage(), ['portal']);
    await adoptLocalRecords(merged);
    reRender();
    flash(root, t(merged ? doneMergedKey : doneKey, { name: player.nickname }));
    return true;
  };

  root.querySelectorAll<HTMLElement>('[data-login]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const label = btn.textContent;
      btn.setAttribute('disabled', '');
      btn.textContent = t('portal.passkey.working');
      void loginWithPasskey().then(async (res) => {
        btn.removeAttribute('disabled');
        btn.textContent = label;
        if (!res.ok) {
          if (res.reason === 'cancelled') return; // 「やめた」は失敗ではない
          flash(root, t('portal.login.failed'));
          return;
        }
        await finishLogin(res.player, 'portal.login.done', 'portal.login.doneMerged');
      });
    });
  });
  root.querySelectorAll<HTMLElement>('[data-transfer-issue]').forEach((btn) => {
    btn.addEventListener('click', () => void showTransferCode(getPlayerId()));
  });
  root.querySelectorAll<HTMLElement>('[data-transfer-use]').forEach((btn) => {
    btn.addEventListener('click', () => {
      void promptTransferCode().then(async (player) => {
        if (!player) return; // やめた／入力できなかった（モーダルが自分で案内済み）
        const adopted = await finishLogin(
          player,
          'portal.transfer.done',
          'portal.transfer.doneMerged',
        );
        // コードで入れた端末に**パスキーを勧める**（docs/AUTH.md の D フロー）。
        // 次からはコード無しで入れる＝紙の控えに頼らなくてよくなる。
        if (adopted && isPasskeySupported()) flash(root, t('portal.transfer.passkeyHint'));
      });
    });
  });
  root.querySelectorAll<HTMLElement>('[data-resume]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const label = btn.textContent;
      btn.setAttribute('disabled', '');
      btn.textContent = t('portal.passkey.working');
      void loginWithPasskey().then(async (res) => {
        btn.removeAttribute('disabled');
        btn.textContent = label;
        if (!res.ok) {
          if (res.reason === 'cancelled') return;
          flash(root, t('portal.login.failed'));
          return;
        }
        // 🔴 **入り直しであって、乗り換えではない**。別アカウントのパスキーで通ってしまうと、
        //    この端末に残っている前の持ち主の記録が、そのまま相手のアカウントへ統合される
        //    （1端末1アカウント・docs/AUTH.md「対象外とする局面」）。
        //    ⇒ 同じ人であることを確かめてから受け入れる。切り替えたいなら先にログアウト。
        if (res.player.id !== getPlayerId()) {
          flash(root, t('portal.resume.otherAccount'));
          return;
        }
        // 同じ人なので統合は起こりえない（記録の行き先は変わらない）。
        // 🔴 **それでも★は送り直す**。入り直しが要るということは**セッションが切れていた**＝
        //    その間のクリアは `submitScore` が 401 で黙って捨てられている。
        //    ⚠️ 進捗は `pushQueue` が次の保存で送り直すが、**`scores` は「クリアした瞬間」しか
        //    書かれない**ので、送り直す機会がここ以外に無い（2026-09-09）。
        await adoptLocalRecords(false);
        reRender();
        flash(root, t('portal.resume.done'));
      });
    });
  });
  // モードの切り替え（ゲームをえらぶ / メダルであそぶ）。
  // ⚠️ **探す範囲をこのブロックに閉じる**——ランキングのタブと同じ轍を踏まない。
  const modeTabs = root.querySelectorAll<HTMLButtonElement>('.hs-mtab');
  const modeHost = modeTabs[0]?.closest<HTMLElement>('div') ?? root;
  const modePanes = modeHost.parentElement?.querySelectorAll<HTMLElement>('[data-mode-pane]');
  modeTabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      modeTabs.forEach((x) => x.setAttribute('aria-selected', String(x === tab)));
      modePanes?.forEach((p) => (p.hidden = p.dataset.modePane !== tab.dataset.mode));
    });
  });

  root.querySelectorAll<HTMLElement>('[data-medal]').forEach((btn) => {
    btn.addEventListener('click', () => {
      // 🔴 **押されたときに初めて読む**。ここで静的 import に戻すと、遊ばない人にも載る。
      if (btn.dataset.medal === 'chinchiro') {
        void import('@/platform/chinchiro/chinchiroScreen').then((m) =>
          m.openChinchiroScreen().then(() => reRender()),
        );
        return;
      }
      // 🔴 **押されたときに初めて読む**。ポーカーは鍵を持つ人しか遊べない（決定7）のに、
      //   静的 import だと**全員の初期JSに載る**（実測 gzip +1.70kB／ポータル全体が 16.2kB
      //   しかないので1割を超える）。⚠️ ポータルの軽さは「遊ぶ」を押すまで Phaser を
      //   落とさない設計と同じ土俵の話なので、ここで崩さない。
      // 閉じたら描き直す＝**メダルのランキングに自分の増減を反映させる**
      // （手持ちは台帳で動くので、遊んだ直後の表示が古いままだと食い違って見える）。
      void import('@/platform/poker/pokerScreen').then((m) => m.openPokerScreen().then(() => reRender()));
    });
  });
  root.querySelectorAll<HTMLElement>('[data-devices]').forEach((btn) => {
    btn.addEventListener('click', () => {
      void openDevices().then((loggedOutAll) => {
        if (!loggedOutAll) return;
        // 全端末を切ったので**この端末も含まれる**。ローカルも片付けてゲストへ戻す
        // （切れたセッションを持ったまま登録済みの顔で居続けると、次の操作が全部 401 になる）。
        void logoutThisDevice().then(() => {
          reRender();
          flash(root, t('portal.devices.loggedOutAll'));
        });
      });
    });
  });
  root.querySelectorAll<HTMLElement>('[data-logout]').forEach((btn) => {
    btn.addEventListener('click', () => {
      void confirmLogout().then((out) => {
        if (!out) return;
        reRender(); // ゲスト状態へ戻る（プレイヤーIDも消えたので次回は新しいIDになる）
        flash(root, t('portal.logout.done'));
      });
    });
  });
  root.querySelectorAll<HTMLElement>('[data-delete]').forEach((btn) => {
    btn.addEventListener('click', () => {
      void confirmDeleteData().then((deleted) => {
        if (!deleted) return;
        reRender(); // ゲスト状態に戻る（プレイヤーIDも消えたので次回は新しいIDになる）
        flash(root, t('portal.delete.done'));
      });
    });
  });

  wireRankingTabs(root);
  wireRankingGame(root, country);
  void loadRankings(root, country);
  void loadPopular(root);
  if (registered) void revealResumeIfNeeded(root);
}

/**
 * 「登録済みなのにセッションが切れている」ときだけ、入り直しのボタンを見せる。
 *
 * ⚠️ **切れていることが分かったときだけ出す**。分からない（オフライン等）ときは出さない
 *    ——`fetchSessionState` がそういう既定になっている。
 * ⚠️ セッションが別人のものだった場合も出す。押せば同じ人のパスキーで入り直せる
 *    （違う人のまま何をしても 401 になるので、出さない方が行き止まりになる）。
 */
async function revealResumeIfNeeded(root: HTMLElement): Promise<void> {
  const btn = root.querySelector<HTMLElement>('[data-resume]');
  if (!btn) return; // パスキー非対応の環境。押しても必ず失敗するボタンは出さない
  if (!shouldOfferResume(await fetchSessionState(), getPlayerId())) return;
  btn.removeAttribute('hidden');
}

/**
 * ログイン直後にサーバーの状態を取り込む（`setPlayerId` などを済ませた後に呼ぶこと）。
 *
 * - **設定** … サーバーを採用（そのアカウントとして遊ぶため）。
 * - **進捗** … サーバーを採用。統合済みならこの端末の記録もサーバー側に入っている。
 * - 🔴 **`claimedShapes` だけは和集合**（docs/AUTH.md 統合規則）。設定ブロブに同居しているが
 *   意味は進捗（＝UnLock を押した記録）なので、上書きすると解錠が巻き戻る。
 *   和集合そのものは `applyClaimed` が `reconcileSettingsWithServer` の中で取っているが、
 *   **送り返さないとサーバーに残らない**ので統合したときだけ push し直す。
 */
/**
 * 🔴 **アカウントに入った直後の共通処理**。入口は3つある（**登録／プロフィール編集** ・
 * **ログイン** ・ **入り直し**）が、やることは同じなので**必ずここを通す**。
 *
 * 🔴 **`adoptServerState` を直接呼ばないこと**。2026-09-09 に本番で、
 * **登録側だけ★を送り直していて、ログインと入り直しが送っていない**のが見つかった
 * （カックロを8面クリアした端末でログインしたのに `scores` が **0 行**のまま＝
 * ⚠️ **進捗は `reconcileProgressWithServer` の初回シードが押し上げるので、
 * 「統合できている」ように見えてランキングにだけ出ない**）。
 * 💡 **3つを1本にまとめたのは、片方だけ直すという直し方をできなくするため**
 * ——2026-08-08 に登録側を直したときは、残り2つが同じ穴だと気づけなかった。
 *
 * ⚠️ **呼ぶのは `setPlayerId` より後**。`submitScore` は `getPlayerId()` を読むので、
 * 先に呼ぶと**ゲスト時代のIDへ送ってしまい、アカウントには一生載らない**。
 *
 * 💡 送り直しは**何度走っても同じ**（`/api/score` は `MAX` upsert）なので門が要らない。
 */
async function adoptLocalRecords(merged: boolean): Promise<void> {
  // ⚠️ 記録は localStorage のゲーム別進捗＝**プレイヤーIDでは分かれていない**ので、
  //    ID を差し替えたあとに集め直しても中身は変わらない。
  const local = await collectLocalRecords();
  if (hasContributions(local)) await backfillScores(local);
  await adoptServerState(merged);
}

async function adoptServerState(merged: boolean): Promise<void> {
  try {
    await reconcileSettingsWithServer();
    if (merged) await pushSettingsToServer();
  } catch {
    /* オフラインなら次回の同期で追いつく */
  }
  for (const g of GAMES) {
    try {
      await g.reconcileProgress?.();
    } catch {
      /* 1本失敗しても他のゲームは取り込む。次回の起動でも再同期される */
    }
  }
}

/** 一度きりの完了通知を数秒だけ出す（削除のような「戻せない操作」の結果を確かに見せるため）。 */
function flash(root: HTMLElement, text: string): void {
  const shell = root.querySelector('.hs-shell');
  if (!shell) return;
  const bar = document.createElement('div');
  bar.className = 'hs-flash';
  bar.setAttribute('role', 'status');
  bar.textContent = text;
  shell.prepend(bar);
  setTimeout(() => bar.remove(), 6000);
}

// ---------- ランキング ----------

function wireRankingTabs(root: HTMLElement): void {
  const tabs = root.querySelectorAll<HTMLButtonElement>('.hs-tab');
  // 🔴 **探す範囲をタブ自身のパネルに限る**（2026-09-11 に本番のバグとして発見）。
  //    `root` 全体から `[data-pane]` を集めると、⚠️ **隣のパネルの
  //    「人気ゲーム」（`data-pane="popular"`）まで巻き込んで消していた**
  //    ——タブを1つ押しただけで、関係の無いパネルが空になる。
  //    本番で再現を確認済み（国別を押す → popular:shown → popular:hidden）。
  //    💡 **タブが増えるほど踏みやすくなる**ので、メダルのタブを足す前にここを直した。
  const panel = tabs[0]?.closest<HTMLElement>('.hs-panel') ?? root;
  const panes = panel.querySelectorAll<HTMLElement>('[data-pane]');
  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      tabs.forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
      const target = tab.dataset.tab;
      panes.forEach((p) => (p.hidden = p.dataset.pane !== target));
    });
  });
}

/**
 * ランキングを絞るゲームの選択（2026-08-10 追加）。
 *
 * 🔴 **★は全ゲーム共通の通貨**なので、横断ランキングだけだと
 *   「たくさん本数を遊んだ人」が必ず上に来て、**1本をやり込んだ人が見えない**。
 *   本数が増えるほどこの偏りは強くなる（3本目の公開で顕在化した）。
 *   API は最初から `game` パラメータを持っていたので、足りなかったのは選ぶ口だけ。
 *
 * ⚠️ **タブではなく選択肢にする**。10本構想（docs/GAMES-ROADMAP.md）まで増えると
 *   タブでは横に並びきらない。
 */
function wireRankingGame(root: HTMLElement, myCountry: string | null): void {
  const sel = root.querySelector<HTMLSelectElement>('[data-rank-game]');
  if (!sel) return;
  sel.addEventListener('change', () => {
    void loadRankings(root, myCountry, sel.value || undefined);
  });
}

/** 副題は選択と**必ず一緒に動かす**（片方だけ変えると表示が中身と食い違う）。 */
function updateRankSubtitle(root: HTMLElement, gameKey?: string): void {
  const sub = root.querySelector<HTMLElement>('.hs-psub');
  if (!sub) return;
  sub.textContent = gameKey
    ? t('portal.rank.subtitleGame', { game: t(`portal.game.${gameKey}.name`) })
    : t('portal.rank.subtitle');
}

async function loadRankings(
  root: HTMLElement,
  myCountry: string | null,
  gameKey?: string,
): Promise<void> {
  const playerPane = root.querySelector<HTMLElement>('[data-pane="player"]');
  const countryPane = root.querySelector<HTMLElement>('[data-pane="country"]');
  const medalsPane = root.querySelector<HTMLElement>('[data-pane="medals"]');
  const balancePane = root.querySelector<HTMLElement>('[data-pane="balance"]');
  if (!playerPane || !countryPane || !medalsPane || !balancePane) return;

  updateRankSubtitle(root, gameKey);
  const loading = emptyRow(t('portal.rank.loading'));
  playerPane.innerHTML = loading;
  countryPane.innerHTML = loading;
  medalsPane.innerHTML = loading;
  balancePane.innerHTML = loading;

  try {
    // `gameKey` 省略 ＝ 全ゲーム横断（API の既定）。渡すとそのゲームだけ。
    // **プレイヤー別と国別の両方に同じ絞りを掛ける**＝タブを切り替えても
    // 見ている対象が変わらない（片方だけ絞ると数字が噛み合わない）。
    // 🔴 **手持ちだけは `gameKey` を渡さない**。台帳（ポーカーの増減）はゲームに属さないので、
    //   ゲーム別の手持ちという量が存在しない（サーバーも `game` 付きを 400 で断る）。
    //   ⚠️ 絞っているときに黙って全体の数字を出すと**絞ったつもりの数字**に見えるので、
    //   下で「この表だけは絞りが効かない」と明示する。
    const [players, countries, medals, balances] = await Promise.all([
      fetchGlobalLeaderboard(10, gameKey),
      fetchCountryLeaderboard(10, gameKey),
      fetchMedalLeaderboard(10, gameKey),
      fetchBalanceLeaderboard(10),
    ]);
    playerPane.innerHTML = players.length
      ? players.map((e, i) => playerRow(e, i + 1)).join('')
      : emptyRow(t('portal.rank.emptyGlobal'));
    countryPane.innerHTML = countries.length
      ? countries.map((e, i) => countryRow(e, i + 1, myCountry)).join('')
      : emptyRow(t('portal.rank.emptyCountry'));
    // ⚠️ **空は異常ではない**（まだ誰も鍵を作っていないだけ）。理由まで書く。
    medalsPane.innerHTML = medals.length
      ? medals.map((e, i) => medalsRow(e, i + 1)).join('')
      : emptyRow(t('portal.rank.emptyMedals'));
    const notFiltered = gameKey ? emptyRow(t('portal.rank.balanceAllGames')) : '';
    balancePane.innerHTML =
      notFiltered +
      (balances.length
        ? balances.map((e, i) => balanceRow(e, i + 1)).join('')
        : emptyRow(t('portal.rank.emptyMedals')));
  } catch {
    const msg = emptyRow(t('portal.rank.error'));
    playerPane.innerHTML = msg;
    countryPane.innerHTML = msg;
    medalsPane.innerHTML = msg;
    balancePane.innerHTML = msg;
  }
}

/**
 * 人気ゲームランキング（2026-08-20 追加）。**グローバルランキングとは別に読む**。
 *
 * ⚠️ 片方が落ちてももう片方は出す＝`loadRankings` と束ねない。
 *   ★の表とプレイ数の表は集計元が別（`scores` / `player_stage_progress`）で、
 *   一方だけ落ちることが実際にありうる。
 * ⚠️ **ゲーム絞りの選択には反応しない**（絞ると1行の表になるだけなので）。
 */
async function loadPopular(root: HTMLElement): Promise<void> {
  const pane = root.querySelector<HTMLElement>('[data-pane="popular"]');
  if (!pane) return;
  try {
    const { totalPlays, entries } = await fetchPopularGames(10);
    // 🔴 **1回も遊ばれていないうちは「0%」を5本並べない**。左外部結合で行そのものは
    //   必ず返ってくる（0回の新作を消さないため）ので、`entries.length` では空を判定できない。
    //   率の表で分母が 0 なら、出せる情報は「まだ誰も遊んでいない」だけ。
    if (totalPlays <= 0 || entries.length === 0) {
      pane.innerHTML = emptyRow(t('portal.popular.empty'));
      return;
    }
    const percents = sharePercents(entries.map((e) => e.plays ?? 0), totalPlays);
    pane.innerHTML = entries.map((e, i) => popularRow(e, i + 1, percents[i] ?? 0)).join('');
  } catch {
    pane.innerHTML = emptyRow(t('portal.rank.error'));
  }
}

function medalClass(rank: number): string {
  return rank === 1 ? 'm1' : rank === 2 ? 'm2' : rank === 3 ? 'm3' : '';
}

function playerRow(e: GlobalLeaderboardEntry, rank: number): string {
  return `<li class="hs-row">
    <span class="hs-rk ${medalClass(rank)}">${rank}</span>
    <span class="hs-fl">${flagEmoji(e.country)}</span>
    <span class="hs-nm">${escapeHtml(e.nickname)}</span>
    <span class="hs-sc"><span class="hs-st">★</span>${e.totalStars ?? 0}</span>
  </li>`;
}

/**
 * 実績メダルの行。
 * ⚠️ `medalClass` は**順位の色**（1位=金）で、ここで並べている「メダル」＝通貨とは別物。
 *   名前が同じなので、触るときに取り違えないこと。
 */
function medalsRow(e: MedalLeaderboardEntry, rank: number): string {
  return `<li class="hs-row${e.isMe ? ' me' : ''}">
    <span class="hs-rk ${medalClass(rank)}">${rank}</span>
    <span class="hs-fl">${flagEmoji(e.country)}</span>
    <span class="hs-nm">${escapeHtml(e.nickname)}<small>${escapeHtml(t('portal.rank.starsOf', { count: e.totalStars ?? 0 }))}</small></span>
    <span class="hs-sc"><span class="hs-st">◎</span>${(e.medals ?? 0).toLocaleString()}</span>
  </li>`;
}

/**
 * 手持ちメダルの行。**実績との差（ポーカーの収支）を小さく添える**。
 *
 * 💡 手持ちだけだと「★をたくさん持っている人」と「勝った人」が同じ顔になる。
 *    差を出すと、⚠️ **負けている人は負けているとはっきり出る**が、
 *    それが分からないと手持ちランキングは実績ランキングの写しにしか見えない。
 */
function balanceRow(e: BalanceLeaderboardEntry, rank: number): string {
  const diff = (e.balance ?? 0) - (e.achievement ?? 0);
  const sign = diff > 0 ? '+' : '';
  const note = diff === 0 ? '' : `${sign}${diff.toLocaleString()}`;
  return `<li class="hs-row${e.isMe ? ' me' : ''}">
    <span class="hs-rk ${medalClass(rank)}">${rank}</span>
    <span class="hs-fl">${flagEmoji(e.country)}</span>
    <span class="hs-nm">${escapeHtml(e.nickname)}${note ? `<small>${escapeHtml(t('portal.rank.pokerDiff', { n: note }))}</small>` : ''}</span>
    <span class="hs-sc"><span class="hs-st">◎</span>${(e.balance ?? 0).toLocaleString()}</span>
  </li>`;
}

function countryRow(e: CountryLeaderboardEntry, rank: number, mine: string | null): string {
  const isMine = mine && e.country && mine.toUpperCase() === e.country.toUpperCase();
  return `<li class="hs-row${isMine ? ' me' : ''}">
    <span class="hs-rk ${medalClass(rank)}">${rank}</span>
    <span class="hs-fl">${flagEmoji(e.country)}</span>
    <span class="hs-nm">${escapeHtml(countryName(e.country))}<small>${escapeHtml(t('portal.rank.playerCount', { count: e.players ?? 0 }))}</small></span>
    <span class="hs-sc"><span class="hs-st">★</span>${e.totalStars ?? 0}</span>
  </li>`;
}

/**
 * 人気ゲームの1行。**旗の桁にはカバー絵の1文字目**を置く＝カードと同じ顔で並ぶ。
 *
 * 🔴 **名前は翻訳から引く**（`portal.game.<key>.name`）。D1 の `games.name` は日本語で
 *   固定されているので、そのまま出すと英語表示のときだけ日本語が混じる。
 *   ⚠️ ただし**登録簿に無いキー**（サーバーに先に行を入れた新作）は翻訳キーが無く、
 *     `t()` がキー文字列をそのまま返してしまう。そのときだけサーバーの名前を使う。
 */
function popularRow(e: PopularGameEntry, rank: number, percent: number): string {
  const entry = GAMES.find((g) => g.key === e.gameKey);
  const name = entry ? t(`portal.game.${e.gameKey}.name`) : e.name;
  const icon = entry?.card.cover[0]?.char ?? '';
  // 🔴 **遊ばれているのに「0%」と出さない**。総回数が増えるほど下位は 0.x% へ落ちるが、
  //   0% は「一度も遊ばれていない」と読めてしまう＝1回でも回数があれば `<1%` と書く。
  const label =
    (e.plays ?? 0) > 0 && percent === 0
      ? t('portal.popular.shareTiny')
      : t('portal.popular.share', { percent });
  // 帯は率をそのまま横幅に写したもの（回数は出さないので、大小はこれで見せる）。
  // ⚠️ 0回のゲームは中身を描かない（`min-width` があるので、0% でも点が残ってしまう）。
  const fill = (e.plays ?? 0) > 0 ? `<i style="width:${percent}%"></i>` : '';
  return `<li class="hs-row">
    <span class="hs-rk ${medalClass(rank)}">${rank}</span>
    <span class="hs-fl">${escapeHtml(icon)}</span>
    <span class="hs-nm">${escapeHtml(name)}<span class="hs-bar" aria-hidden="true">${fill}</span></span>
    <span class="hs-sc">${escapeHtml(label)}</span>
  </li>`;
}

function emptyRow(text: string): string {
  return `<li class="hs-empty">${escapeHtml(text)}</li>`;
}

// ---------- マークアップ ----------

function markup(nickname: string | null, country: string | null, registered: boolean): string {
  const avaText = registered ? [...(nickname || 'P')][0].toUpperCase() : 'G';
  const whoName = registered ? escapeHtml(nickname!) : escapeHtml(t('portal.who.guest'));
  const whoSub = escapeHtml(
    registered ? t('portal.who.registered') : t('portal.who.unregistered'),
  );
  const flag = flagEmoji(country);

  return `
  <div class="hs-wrap"><div class="hs-shell">

    <header class="hs-top">
      <div class="hs-brand">
        <div class="hs-mark" aria-hidden="true">🎲</div>
        <div>
          <div class="hs-name">HONGSOFT.JP <b>GAMES</b></div>
          <div class="hs-tag">Board &amp; Puzzle Playground</div>
        </div>
      </div>
      <button class="hs-icon" data-settings title="${escapeHtml(t('portal.settings.title'))}"
              aria-label="${escapeHtml(t('portal.settings.title'))}">⚙</button>
      <button class="hs-chip" data-register title="${escapeHtml(t('portal.who.chipTitle'))}">
        <span class="hs-ava">${escapeHtml(avaText)}</span>
        <span class="hs-who"><span class="n">${whoName}</span><span class="s">${whoSub}</span></span>
        <span class="hs-flag">${flag}</span>
      </button>
    </header>

    <section class="hs-welcome">
      <div>
        <h1>${escapeHtml(t('portal.welcome.title'))}</h1>
        <p>${escapeHtml(t('portal.welcome.body'))}</p>
      </div>
      <div class="hs-cta">
        <button class="hs-btn hs-btn-primary" data-play="${DEFAULT_GAME.key}">${escapeHtml(t('portal.cta.playGuest'))}</button>
        <button class="hs-btn hs-btn-ghost" data-register>${escapeHtml(
          registered ? t('portal.cta.editProfile') : t('portal.cta.register'),
        )}</button>
${
  // パスキーは**登録済みプレイヤーにだけ**出す。ゲストにはまだ結び付ける先が無い
  // （先にニックネームを登録してもらう）。対応していない環境では出さない
  // ＝ 押しても必ず失敗するボタンを見せない。
  registered && isPasskeySupported()
    ? `        <button class="hs-btn hs-btn-ghost" data-passkey>${escapeHtml(t('portal.cta.addPasskey'))}</button>`
    : ''
}
${
  // 引き継ぎコードは**パスキーの対応可否で出し分けない**。パスキーが使えない環境の
  // 保険がこれなので、`isPasskeySupported()` で隠すと必要な人にだけ届かなくなる。
  registered
    ? `        <button class="hs-btn hs-btn-ghost" data-transfer-issue>${escapeHtml(t('portal.cta.transferIssue'))}</button>`
    : `        <button class="hs-btn hs-btn-ghost" data-transfer-use>${escapeHtml(t('portal.cta.transferUse'))}</button>`
}
${
  // ログインは**未登録**のときだけ出す。記録がある端末では、持ち主を差し替える前に
  // 統合の確認（`confirmMerge`）が挟まる＝記録が黙って消える経路は作らない。
  // ⚠️ **登録済みプレイヤーには出さない**。別のアカウントへ入り直すには
  //    先にログアウトが要る（1端末1アカウント・docs/AUTH.md「対象外とする局面」）。
  //    ログアウトは実装順序(7)。
  !registered && isPasskeySupported()
    ? `        <button class="hs-btn hs-btn-ghost" data-login>${escapeHtml(t('portal.cta.login'))}</button>`
    : ''
}
${
  // 🔴 **登録済みでセッションが切れた人のための入り直し口**。
  //    `hidden` で置いておき、サーバーに聞いて「本当に切れている」と分かったときだけ出す
  //    （[dashboard の revealResumeIfNeeded](#) 参照）。最初から出すと、
  //    有効な人にも押す理由の無いボタンが並び、生体認証のシートを無意味に開かせる。
  //    ⚠️ **マークアップ側で出し分けられない**——ログイン状態は通信しないと分からず、
  //       描画は同期だから。だから「置いておいて後から見せる」形にしている。
  registered && isPasskeySupported()
    ? `        <button class="hs-btn hs-btn-ghost" data-resume hidden>${escapeHtml(t('portal.cta.resume'))}</button>`
    : ''
}
      </div>
    </section>

    <div class="hs-grid">

      <div>
        <!--
          ランキングのタブ（class="hs-tab"）とはクラスを分ける。同じクラスにすると
          wireRankingTabs が拾い、2026-09-11 に本番で起きた
          「タブを押すと隣のパネルが消える」事故をそのまま再現する。
        -->
        <div class="hs-modes" role="tablist">
          <button type="button" class="hs-mtab" role="tab" data-mode="games" aria-selected="true">${escapeHtml(t('portal.mode.games'))}</button>
          <button type="button" class="hs-mtab" role="tab" data-mode="medals" aria-selected="false">${escapeHtml(t('portal.mode.medals'))}</button>
        </div>

        <div data-mode-pane="games">
        <div class="hs-seclabel"><h2>${escapeHtml(t('portal.section.games'))}</h2><span class="hint">${escapeHtml(
          // ⚠️ 予告が**0本のときに「0 近日公開」と書かない**（2026-09-11 に `pair` を撤回して 0 になった）。
          //   数を差し込む文言は**0 を想定していない**——そのまま出すと「近日公開が0本ある」という
          //   意味を持たない表示になる。⇒ 本数そのものではなく**言う/言わない**を出し分ける。
          UPCOMING.length > 0
            ? t('portal.section.gamesHint', { live: GAMES.length, soon: UPCOMING.length })
            : t('portal.section.gamesHintLive', { live: GAMES.length }),
        )}</span></div>
        <div class="hs-games">
${GAMES.map((g, i) => liveCard(g, i === 0)).join('\n')}
${UPCOMING.map(soonCard).join('\n')}
        </div>
        </div>

        <div data-mode-pane="medals" hidden>
        <div class="hs-seclabel"><h2>${escapeHtml(t('portal.section.medals'))}</h2><span class="hint">${escapeHtml(t('portal.section.medalsHint'))}</span></div>
        <div class="hs-games">
${MEDAL_GAMES.map((g) => medalCard(g, registered)).join('\n')}
        </div>
        </div>
      </div>

      <div class="hs-side">

      <aside class="hs-panel">
        <div class="hs-phead">
          <div class="hs-ptitle"><span class="crown" aria-hidden="true">♛</span><h2>${escapeHtml(t('portal.rank.title'))}</h2></div>
          <p class="hs-psub">${escapeHtml(t('portal.rank.subtitle'))}</p>
        </div>
        <div class="hs-tabs" role="tablist">
          <button class="hs-tab" role="tab" data-tab="player" aria-selected="true">${escapeHtml(t('portal.rank.tabPlayer'))}</button>
          <button class="hs-tab" role="tab" data-tab="country" aria-selected="false">${escapeHtml(t('portal.rank.tabCountry'))}</button>
          <button class="hs-tab" role="tab" data-tab="medals" aria-selected="false">${escapeHtml(t('portal.rank.tabMedals'))}</button>
          <button class="hs-tab" role="tab" data-tab="balance" aria-selected="false">${escapeHtml(t('portal.rank.tabBalance'))}</button>
        </div>
        <div class="hs-gamepick">
          <label class="sr-only" for="hs-rank-game">${escapeHtml(t('portal.rank.gameLabel'))}</label>
          <select id="hs-rank-game" class="hs-gamesel" data-rank-game>
            <option value="">${escapeHtml(t('portal.rank.allGames'))}</option>
${GAMES.map(
  (g) =>
    `            <option value="${escapeHtml(g.key)}">${escapeHtml(t(`portal.game.${g.key}.name`))}</option>`,
).join('\n')}
          </select>
        </div>
        <ol class="hs-rows" data-pane="player"><li class="hs-empty">${escapeHtml(t('portal.rank.loading'))}</li></ol>
        <ol class="hs-rows" data-pane="country" hidden><li class="hs-empty">${escapeHtml(t('portal.rank.loading'))}</li></ol>
        <ol class="hs-rows" data-pane="medals" hidden><li class="hs-empty">${escapeHtml(t('portal.rank.loading'))}</li></ol>
        <ol class="hs-rows" data-pane="balance" hidden><li class="hs-empty">${escapeHtml(t('portal.rank.loading'))}</li></ol>
      </aside>

      <aside class="hs-panel">
        <div class="hs-phead">
          <div class="hs-ptitle"><span class="crown" aria-hidden="true">▲</span><h2>${escapeHtml(t('portal.popular.title'))}</h2></div>
          <p class="hs-psub">${escapeHtml(t('portal.popular.subtitle'))}</p>
        </div>
        <ol class="hs-rows" data-pane="popular"><li class="hs-empty">${escapeHtml(t('portal.rank.loading'))}</li></ol>
      </aside>

      </div>

    </div>

    <footer class="hs-foot">
      <nav class="hs-legal">
        ${legalAnchor('terms')}
        ${legalAnchor('privacy')}
        ${
          // アカウントの後始末は**フッターにまとめる**。上の CTA 列は「遊ぶ・登録・入る」
          // という前向きな導線なので、そこに「ログアウト」を混ぜると押し間違いが出る
          // （「データを削除」が元からここに居るのと同じ理由）。
          registered
            ? `<button type="button" class="hs-footlink" data-devices>${escapeHtml(t('portal.devices.link'))}</button>
        <button type="button" class="hs-footlink" data-logout>${escapeHtml(t('portal.logout.link'))}</button>`
            : ''
        }
        ${
          // 消すものが何も無い初回訪問者には出さない（押しても何も起きないボタンになるため）。
          hasLocalPlayerData()
            ? `<button type="button" class="hs-dangerlink" data-delete>${escapeHtml(t('portal.delete.link'))}</button>`
            : ''
        }
      </nav>
      ${
        // 🔴 **推奨環境は「保証しない範囲」を先に言うためにある**（2026-09-11 判断）。
        //   盤は設計空間 1024×768 を `Scale.FIT` で嵌めるだけなので、
        //   ⚠️ **小さい画面でも崩れず等倍で縮む**＝「動くが読みづらい」という気づきにくい形で劣化する。
        //   実寸は**ビューポートの物理的な高さに正比例**し、10×10 のマスは
        //   iPad Air 11" で約 12.6mm ／ iPad mini 8.3" で約 9.0mm（手がかりの字は 3.8mm → 2.7mm）。
        //   ⇒ **全サイズを作り込むのではなく、推奨を明記する**。
        //   文言は [smallScreenNotice.ts](smallScreenNotice.ts) のオーバーレイと**同じ基準**を指す。
        `<span class="hs-env">${escapeHtml(t('portal.env.note'))}</span>`
      }
      <span class="hs-cr">COPYRIGHT(C) 2026 HONGSOFT.JP ALL RIGHT RESERVED.</span>
    </footer>

  </div></div>`;
}

// ---------- ゲームカード（登録簿から生成） ----------
//
// カードは `GAMES` / `UPCOMING` をそのまま写す。**ゲームを1本足してもここは触らない**。
// 名前と説明は `portal.game.<key>.name` / `.desc` をキーから導く（訳し忘れは
// catalogs.test.ts が検出する）。

/**
 * 遊べるゲームのカバー＝**そのゲームのタイトルロゴ**（2026-08-22 ユーザー指定）。
 *
 * 🔴 それまでは全ゲームが**牌を8枚並べた絵**だった。二角取りのために作った形が
 *   そのまま5本へ写されていて、どのゲームも同じ見た目になっていた。
 *
 * 画像は6枚で **163kB**（初期JS gzip 26kB に対して大きい）。カード用に縮めた複製を
 * `assets/portal/covers/` に置いてこの大きさに収めている（[docs/PORTAL-COVERS.md](../../../docs/PORTAL-COVERS.md)）。
 *
 * ⚠️ **`loading="lazy"` は当てにしないこと**。付けてはあるが、実測では
 *   **6枚とも初回に読まれた**（Chromium の遅延しきい値が広く、ダッシュボード全体が
 *   その範囲に入るため）。狭い画面や遅い回線では効くので付けたままにするが、
 *   「先頭以外は後で読まれる」と考えて枚数や大きさを決めてはいけない。
 * 幅と高さを属性で渡すのは、読み込み前に場所を取らせて**カードが飛び跳ねないため**。
 */
function coverImage(logo: string, eager: boolean): string {
  return `<div class="hs-cover"><img class="hs-logo" src="${escapeHtml(logo)}" alt="" width="520" height="260"
    decoding="async"${eager ? '' : ' loading="lazy"'}></div>`;
}

/**
 * 文字のカバー絵（**近日公開のカードだけ**）。1枚なら中央に大きく1文字、複数なら4列の牌モザイク。
 *
 * 🔴 **牌の幅は固定（34px）なので、文字数で font-size を落とす**。
 *   既定の 26px は麻雀牌やスートの**1文字**を前提にした値で、`2048` のように
 *   数字を並べるゲームでは 3桁が枠から溢れて隣の牌に重なる（2026-08-14 に実際に出た）。
 *   ⚠️ 登録簿側で「2桁までにする」と決める手もあるが、それだと**カバー絵の中身が
 *   platform の都合に縛られる**。字数に応じて縮める規則をここに1つ置くほうが、
 *   後から来るゲーム（ノノグラム等の数字もの）も黙って正しく出る。
 */
function cover(tiles: readonly CoverTile[]): string {
  if (tiles.length === 1) {
    return `<div class="hs-cover"><span class="lock" aria-hidden="true">${escapeHtml(tiles[0].char)}</span></div>`;
  }
  const mj = tiles
    .map((tl) => {
      const ink = tl.ink === 'green' ? ' g' : tl.ink === 'black' ? ' k' : '';
      // 絵文字・麻雀牌はサロゲートペアなので、字数は**コードポイント単位**で数える
      // （`.length` だと 🀇 が 2 文字になり、1文字の牌まで縮んでしまう）。
      const len = [...tl.char].length;
      const fit = len >= 4 ? ' n4' : len === 3 ? ' n3' : len === 2 ? ' n2' : '';
      return `<span class="hs-mj${ink}${fit}">${escapeHtml(tl.char)}</span>`;
    })
    .join('');
  return `<div class="hs-cover"><div class="hs-tiles" aria-hidden="true">${mj}</div></div>`;
}

/** 遊べるゲーム。先頭の1本だけ横長の feature カードにする。 */
/**
 * 「メダルであそぶ」に並ぶもの（決定16 で2本になった）。
 *
 * 🔴 **登録簿（`games/registry.ts`）とは別**。あちらは★を競うゲームの一覧で、
 *    こちらはメダルを賭ける遊び——**遊び方も、載るランキングも違う**。
 *    ⚠️ 片方へ寄せると「★のランキングにポーカーが出る」ような形になる。
 * ⚠️ 3本目を足すときは**ここに1行**と、押したときに開く口（`data-*`）を1つ。
 */
interface MedalGame {
  /** `data-` 属性の名前。押したときの分岐に使う。 */
  key: 'poker' | 'chinchiro';
  logo: string;
  w: number;
  h: number;
}

const MEDAL_GAMES: MedalGame[] = [
  { key: 'poker', logo: POKER_LOGO, w: POKER_LOGO_W, h: POKER_LOGO_H },
  { key: 'chinchiro', logo: CHINCHIRO_LOGO, w: CHINCHIRO_LOGO_W, h: CHINCHIRO_LOGO_H },
];

function medalCard(g: MedalGame, registered: boolean): string {
  // 🔴 **メダルは鍵を持つアカウントのもの**（決定7）。ゲストにも**見せる**が、
  //   遊べる条件を書く＝これ自体が「鍵を作る動機」になる（決定7 が導線の代わりと
  //   位置づけたのはこの形）。⚠️ 押しても何も起きないボタンは出さない。
  const action = registered
    ? `<button class="hs-btn hs-btn-primary" data-medal="${g.key}">${escapeHtml(t(`portal.medalGame.${g.key}.play`))}</button>`
    : `<p class="hs-cardnote">${escapeHtml(t('portal.medalGame.needAccount'))}</p>
                     <button class="hs-btn hs-btn-ghost" data-register>${escapeHtml(t('portal.cta.register'))}</button>`;
  return `          <article class="hs-card feature">
            <!-- ⚠️ ここは既定で hidden の「メダルであそぶ」の中なので、タブを開くまで読まれない。
                 ポータルの初期表示に絵のぶんを足さないため loading="lazy" は必須。 -->
            <div class="hs-cover"><img class="hs-logo" src="${escapeHtml(g.logo)}" alt=""
              width="${g.w}" height="${g.h}" decoding="async" loading="lazy"></div>
            <div class="hs-body">
              <h3>${escapeHtml(t(`portal.medalGame.${g.key}.name`))}</h3>
              <p>${escapeHtml(t(`portal.medalGame.${g.key}.desc`))}</p>
              ${action}
            </div>
          </article>`;
}

function liveCard(g: GameEntry, feature: boolean): string {
  return `<article class="hs-card${feature ? ' feature' : ''}">
    ${coverImage(g.card.logo, feature)}
    <div class="hs-body">
      <h3>${escapeHtml(t(`portal.game.${g.key}.name`))}</h3>
      <p>${escapeHtml(t(`portal.game.${g.key}.desc`))}</p>
      <div class="hs-meta">
        <span class="hs-pill live"><span class="dot"></span>${escapeHtml(t('portal.pill.live'))}</span>
        ${g.card.ranked ? `<span class="hs-pill star">${escapeHtml(t('portal.pill.ranked'))}</span>` : ''}
        <button class="hs-btn hs-btn-primary sm" data-play="${escapeHtml(g.key)}" style="margin-left:auto;">${escapeHtml(t('portal.btn.play'))}</button>
      </div>
    </div>
  </article>`;
}

/** 近日公開。押せるボタンは無い。 */
function soonCard(u: UpcomingEntry): string {
  return `<article class="hs-card soon">
    ${cover(u.cover)}
    <div class="hs-body">
      <h3>${escapeHtml(t(`portal.game.${u.key}.name`))}</h3>
      <p>${escapeHtml(t(`portal.game.${u.key}.desc`))}</p>
      <div class="hs-meta">
        <span class="hs-pill soon">${escapeHtml(t('portal.pill.soon'))}</span>
        <button class="hs-btn sm disabled" disabled>${escapeHtml(t('portal.btn.preparing'))}</button>
      </div>
    </div>
  </article>`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );
}

// ---------- スタイル（1回だけ注入） ----------

let cssInjected = false;
function injectCss(): void {
  if (cssInjected) return;
  cssInjected = true;
  const style = document.createElement('style');
  style.id = 'hs-dashboard-css';
  style.textContent = DASHBOARD_CSS;
  document.head.append(style);
}

const DASHBOARD_CSS = `
:root {
  --hs-bg:#F0E9DA; --hs-panel:#FBF8F1; --hs-tile:#FFFDF8; --hs-tileink:#26312B;
  --hs-ink:#22302B; --hs-inksoft:#5E6C63; --hs-inkfaint:#8A9389; --hs-line:#E3DAC7;
  --hs-linesoft:#EEE7D8; --hs-accent:#0E9E68; --hs-accentdk:#0A6E49; --hs-accentsf:#DDF0E5;
  --hs-gold:#E1971A; --hs-felt:#1E7A56;
  --hs-fdisplay: ui-rounded, "SF Pro Rounded", "Hiragino Maru Gothic ProN", system-ui, sans-serif;
  --hs-fbody: system-ui, -apple-system, "Hiragino Kaku Gothic ProN", "Yu Gothic", Meiryo, sans-serif;
  --hs-fnum: ui-monospace, "SF Mono", Menlo, monospace;
}
@media (prefers-color-scheme: dark) {
  :root {
    --hs-bg:#14201C; --hs-panel:#1C2A25; --hs-tile:#F6F1E6; --hs-tileink:#26312B;
    --hs-ink:#E9EFE9; --hs-inksoft:#A6B2AA; --hs-inkfaint:#778176; --hs-line:#2C3B34;
    --hs-linesoft:#243029; --hs-accent:#2BC488; --hs-accentdk:#7FE3B6; --hs-accentsf:#163027;
    --hs-gold:#F0B33F; --hs-felt:#2BC488;
  }
}
.hs-wrap * { box-sizing: border-box; }
.hs-wrap {
  font-family: var(--hs-fbody); color: var(--hs-ink); line-height: 1.5;
  background: radial-gradient(1200px 520px at 82% -8%, color-mix(in srgb, var(--hs-felt) 12%, transparent), transparent 60%), var(--hs-bg);
  min-height: 100%;
  -webkit-font-smoothing: antialiased;
}
.hs-shell { max-width: 1120px; margin: 0 auto; padding: 18px clamp(14px, 4vw, 40px) 48px; }
.hs-wrap button { font-family: inherit; }
.hs-wrap :focus-visible { outline: 3px solid color-mix(in srgb, var(--hs-accent) 60%, transparent); outline-offset: 2px; border-radius: 6px; }

.hs-top { display: flex; align-items: center; gap: 14px; padding: 8px 2px 16px; flex-wrap: wrap; }
.hs-brand { display: flex; align-items: center; gap: 12px; margin-right: auto; }
.hs-mark { width: 44px; height: 44px; border-radius: 12px; display: grid; place-items: center; background: var(--hs-felt); color: #FFFDF8; font-size: 24px; box-shadow: inset 0 0 0 2px rgba(255,255,255,.14), 0 6px 14px rgba(22,48,38,.18); }
.hs-name { font-family: var(--hs-fdisplay); font-weight: 700; font-size: 20px; line-height: 1.05; }
.hs-name b { color: var(--hs-accentdk); }
.hs-tag { font-size: 11px; color: var(--hs-inksoft); letter-spacing: .06em; text-transform: uppercase; }
.hs-chip { display: flex; align-items: center; gap: 10px; background: var(--hs-panel); border: 1px solid var(--hs-line); border-radius: 999px; padding: 6px 12px 6px 6px; cursor: pointer; }
.hs-chip:hover { border-color: var(--hs-accent); }
    /* 設定の歯車。⚠️ 44px はタップの下限なので、絵を縮めてもここは割らない。 */
    .hs-icon {
      flex: 0 0 auto; width: 44px; height: 44px; padding: 0; font-size: 22px; line-height: 1;
      background: var(--hs-panel); color: var(--hs-ink); cursor: pointer;
      border: 1px solid var(--hs-line); border-radius: 999px; display: grid; place-items: center;
    }
    .hs-icon:hover { border-color: var(--hs-accent); }
    .hs-icon:active { transform: scale(.94); }
.hs-ava { width: 32px; height: 32px; border-radius: 999px; display: grid; place-items: center; background: linear-gradient(150deg, var(--hs-accent), var(--hs-felt)); color: #fff; font-family: var(--hs-fdisplay); font-weight: 700; font-size: 15px; }
.hs-who { line-height: 1.15; text-align: left; }
.hs-who .n { display: block; font-weight: 650; font-size: 13.5px; color: var(--hs-ink); }
.hs-who .s { display: block; font-size: 11px; color: var(--hs-inksoft); }
.hs-flag { font-size: 16px; }

.hs-welcome { display: flex; align-items: center; gap: 16px 24px; flex-wrap: wrap; background: var(--hs-panel); border: 1px solid var(--hs-line); border-radius: 16px; padding: 18px 22px; margin-bottom: 24px; position: relative; overflow: hidden; }
.hs-welcome::before { content: ""; position: absolute; inset: 0 auto 0 0; width: 6px; background: linear-gradient(var(--hs-accent), var(--hs-felt)); }
.hs-welcome h1 { font-family: var(--hs-fdisplay); font-weight: 700; font-size: clamp(18px, 2.4vw, 23px); margin: 0 0 3px; text-wrap: balance; }
.hs-welcome p { margin: 0; color: var(--hs-inksoft); font-size: 13px; }
.hs-cta { margin-left: auto; display: flex; gap: 10px; flex-wrap: wrap; }

.hs-btn { font-family: var(--hs-fdisplay); font-weight: 700; font-size: 14.5px; border: 0; cursor: pointer; border-radius: 12px; padding: 12px 20px; display: inline-flex; align-items: center; gap: 8px; transition: transform .12s ease, box-shadow .12s ease, background .12s ease; color: var(--hs-ink); }
.hs-btn-primary { background: var(--hs-accent); color: #fff; box-shadow: 0 8px 18px color-mix(in srgb, var(--hs-accent) 40%, transparent); }
@media (hover: hover) and (pointer: fine) {
  .hs-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 12px 22px color-mix(in srgb, var(--hs-accent) 48%, transparent); }
}
.hs-btn-ghost { background: transparent; border: 1px solid var(--hs-line); }
.hs-btn-ghost:hover { border-color: var(--hs-accent); color: var(--hs-accentdk); }
.hs-btn.sm { padding: 9px 16px; font-size: 13px; border-radius: 10px; }
.hs-btn.disabled { background: color-mix(in srgb, var(--hs-line) 60%, transparent); color: var(--hs-inkfaint); cursor: not-allowed; box-shadow: none; }

.hs-grid { display: grid; grid-template-columns: minmax(0, 1fr) 336px; gap: 24px; align-items: start; }
@media (max-width: 860px) { .hs-grid { grid-template-columns: 1fr; } }

.hs-seclabel { display: flex; align-items: baseline; gap: 10px; margin: 2px 2px 12px; }
.hs-seclabel h2 { font-family: var(--hs-fdisplay); font-weight: 700; font-size: 14px; letter-spacing: .12em; text-transform: uppercase; margin: 0; }
.hs-seclabel .hint { font-size: 12px; color: var(--hs-inkfaint); margin-left: auto; }

.hs-games { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
@media (max-width: 560px) { .hs-games { grid-template-columns: 1fr; } }
.hs-card { background: var(--hs-tile); color: var(--hs-tileink); border-radius: 16px; border: 1px solid color-mix(in srgb, var(--hs-line) 70%, transparent); box-shadow: 0 2px 0 color-mix(in srgb, var(--hs-tileink) 8%, transparent), 0 10px 22px rgba(22,48,38,.12); overflow: hidden; display: flex; flex-direction: column; }

/* 🔴 **カードはダークでも紙の色のまま**（--hs-tile は明るい側でも暗い側でも明るい）。
   ⇒ カードの中で**画面の地のトークン**（--hs-ink / --hs-inksoft）を使ってはいけない。
   ⚠️ 2026-09-17 に実機で発生: .hs-btn が color: var(--hs-ink) を持っていたため、
      ダークで **白い字を紙に置く**形になり（#E9EFE9 を #F6F1E6 の上＝コントラスト 1.03:1）、
      「メダルであそぶ」の登録ボタンが**枠だけの空の矩形**に見えた。
      💡 押せはするので**例外も警告も出ない**。見た人が「壊れている」と気づくしかない。
   ※ アクセント地の上（primary）だけは白のままでよい＝地が自前で暗いため。 */
.hs-card .hs-btn { color: var(--hs-tileink); }
.hs-card .hs-btn-primary { color: #fff; }
.hs-card .hs-btn.disabled { color: color-mix(in srgb, var(--hs-tileink) 55%, transparent); }
.hs-card.feature { grid-column: 1 / -1; flex-direction: row; }
@media (max-width: 560px) { .hs-card.feature { flex-direction: column; } }
/* 🔴 **持ち上がる演出はマウスのときだけ**（2026-08-22 ユーザー指摘）。
   指で触る端末では :hover が**タップした後も貼り付いたまま残る**ので、
   カードが「選択状態」になったように見える。押しても何も起きない飾りなので用が無く、
   そのうえ指を置いた瞬間にカードが 4px 動くため、**そのまま引いてもスクロールにならない**
   （画面を送ろうとして送れない、という形で出ていた）。
   ✅ この因果は **iPad 実機で裏が取れている**（2026-08-22。ここを直したらスワイプで
     滑らかにスクロールするようになった、とユーザーが確認）。⚠️ **マウスのブラウザでは
     再現できない**ので、直ったことを手元で確かめようとしないこと（当時 CDP で
     hover:none / pointer:coarse を強制しても効かなかった）。
   ⚠️ hover を消すだけでは足りない。触った直後は :hover と同じ見た目が一瞬付くので、
     カードの transition も指の端末では切る（下の hs-card 側）。 */
@media (hover: hover) and (pointer: fine) {
  .hs-card { transition: transform .14s ease, box-shadow .14s ease; }
  .hs-card:not(.soon):hover { transform: translateY(-4px); box-shadow: 0 4px 0 color-mix(in srgb, var(--hs-tileink) 8%, transparent), 0 18px 34px rgba(22,48,38,.2); }
}
.hs-cover { background: radial-gradient(120% 120% at 20% 0%, color-mix(in srgb, var(--hs-felt) 22%, #2E9E77), var(--hs-felt)); display: grid; place-content: center; padding: 20px; min-height: 128px; }
.hs-card.feature .hs-cover { min-width: 232px; width: 42%; }
@media (max-width: 560px) { .hs-card.feature .hs-cover { width: auto; } }
.hs-tiles { display: grid; grid-template-columns: repeat(4, 1fr); gap: 5px; }
.hs-mj { width: 34px; height: 44px; border-radius: 7px; background: #FFFDF8; color: #B23A2E; display: grid; place-items: center; font-size: 26px; line-height: 1; box-shadow: 0 2px 0 #C9BEA6, 0 3px 6px rgba(0,0,0,.25); }
.hs-mj.g { color: #1E7A56; } .hs-mj.k { color: #26312B; }
/* 牌の幅は 34px 固定。字数が増えたら文字を縮めて収める（cover() の 🔴 参照）。 */
.hs-mj.n2 { font-size: 19px; font-weight: 700; }
.hs-mj.n3 { font-size: 14px; font-weight: 700; letter-spacing: -.3px; }
.hs-mj.n4 { font-size: 11px; font-weight: 700; letter-spacing: -.4px; }
.hs-cover .lock { font-size: 40px; opacity: .9; }
/* ロゴは枠に収める。**縦横比は変えない**（object-fit: contain）＝
   横長のロゴも正方形に近いタイトル画面も、同じ枠へそのまま素直に入る。
   ⚠️ 画像は装飾なので alt は空（ゲーム名はすぐ下の見出しが持っている）。
   指で触ったときに画像だけが選ばれる／長押しで保存メニューが出るのを止める。
   ⚠️ このCSSは TS のテンプレート文字列の中なので、コメントにバッククォートは書けない。 */
.hs-logo { max-width: 100%; max-height: 126px; width: auto; height: auto; display: block;
  object-fit: contain; border-radius: 8px; filter: drop-shadow(0 3px 8px rgba(0,0,0,.28));
  -webkit-user-drag: none; -webkit-touch-callout: none; pointer-events: none; }
.hs-card.feature .hs-logo { max-height: 166px; }
.hs-card .hs-body { padding: 16px 18px 18px; display: flex; flex-direction: column; gap: 8px; flex: 1; }
.hs-card .hs-body h3 { font-family: var(--hs-fdisplay); font-weight: 700; font-size: 19px; margin: 0; }
.hs-card.feature .hs-body h3 { font-size: 22px; }
.hs-card .hs-body p { margin: 0; font-size: 13px; color: color-mix(in srgb, var(--hs-tileink) 68%, transparent); }
.hs-meta { display: flex; align-items: center; gap: 8px; margin-top: auto; padding-top: 6px; flex-wrap: wrap; }
.hs-pill { font-size: 11px; font-weight: 700; letter-spacing: .04em; padding: 4px 10px; border-radius: 999px; display: inline-flex; align-items: center; gap: 5px; }
.hs-pill.live { background: var(--hs-accentsf); color: var(--hs-accentdk); }
.hs-pill.live .dot { width: 7px; height: 7px; border-radius: 999px; background: var(--hs-accent); }
.hs-pill.soon { background: #F1EADB; color: #97836A; }
.hs-pill.star { background: color-mix(in srgb, var(--hs-gold) 18%, transparent); color: #97701A; }
.hs-card.soon { opacity: .82; }
.hs-card.soon .hs-cover { background: repeating-linear-gradient(135deg, #E9E1D0, #E9E1D0 12px, #E4DAC6 12px, #E4DAC6 24px); }

/* 右カラムは板を縦に積む（グローバルランキングの下に人気ゲームランキング）。 */
.hs-side { display: flex; flex-direction: column; gap: 24px; }
/* ⚠️ 板の position:sticky は 2026-08-20 に降ろした（このCSSは TS のテンプレート文字列の
   中なので、コメントにもバッククォートは書けない）。板が2枚になり、積んだ高さが
   画面より高くなると、貼り付いた列の下側に指が届かなくなるため（1枚のときは問題なかった）。 */
.hs-panel { background: var(--hs-panel); border: 1px solid var(--hs-line); border-radius: 16px; overflow: hidden; }
.hs-phead { padding: 16px 18px 0; }
.hs-ptitle { display: flex; align-items: center; gap: 8px; }
.hs-ptitle h2 { font-family: var(--hs-fdisplay); font-weight: 700; font-size: 15.5px; margin: 0; }
.hs-ptitle .crown { color: var(--hs-gold); font-size: 16px; }
.hs-psub { font-size: 11.5px; color: var(--hs-inkfaint); margin: 2px 0 12px; }
.hs-modes { display: flex; gap: 6px; margin-bottom: 12px; }
.hs-mtab {
  font-family: var(--hs-fdisplay); font-weight: 800; font-size: 14px; cursor: pointer;
  padding: 9px 16px; border-radius: 999px; border: 1px solid var(--hs-line);
  background: var(--hs-panel); color: var(--hs-inksoft); min-height: 40px;
}
.hs-mtab[aria-selected="true"] {
  background: var(--hs-accent); border-color: var(--hs-accent); color: #fff;
}
/* ⚠️ これもカードの中＝紙の上。--hs-inksoft（画面の地の色）はダークで薄すぎる（約2:1）。 */
.hs-cardnote { margin: 0 0 8px; color: color-mix(in srgb, var(--hs-tileink) 72%, transparent); font-size: 13px; }
.hs-tabs { display: flex; gap: 4px; padding: 0 12px; }
.hs-tab { flex: 1; text-align: center; font-family: var(--hs-fdisplay); font-weight: 700; font-size: 12.5px; padding: 9px 8px; border: 0; background: transparent; color: var(--hs-inksoft); cursor: pointer; border-radius: 10px 10px 0 0; border-bottom: 2px solid transparent; }
.hs-tab[aria-selected="true"] { color: var(--hs-accentdk); border-bottom-color: var(--hs-accent); background: color-mix(in srgb, var(--hs-accent) 8%, transparent); }
.hs-gamepick { padding: 8px 12px 0; }
.hs-gamesel { width: 100%; font-family: inherit; font-size: 12.5px; font-weight: 600; color: var(--hs-ink);
  background: var(--hs-paper); border: 1px solid var(--hs-line); border-radius: 8px; padding: 7px 9px; cursor: pointer; }
.hs-gamesel:focus-visible { outline: 2px solid var(--hs-accent); outline-offset: 1px; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden;
  clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
.hs-rows { list-style: none; margin: 0; padding: 8px; }
.hs-row { display: grid; grid-template-columns: 26px 22px 1fr auto; align-items: center; gap: 10px; padding: 9px 10px; border-radius: 10px; }
.hs-row + .hs-row { border-top: 1px solid var(--hs-linesoft); }
.hs-rk { font-family: var(--hs-fnum); font-weight: 700; font-size: 13px; color: var(--hs-inksoft); text-align: center; font-variant-numeric: tabular-nums; }
.hs-rk.m1 { color: var(--hs-gold); } .hs-rk.m2 { color: #9AA6AD; } .hs-rk.m3 { color: #C08457; }
.hs-fl { font-size: 17px; text-align: center; }
.hs-nm { font-size: 13.5px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--hs-ink); }
.hs-nm small { display: block; font-weight: 500; font-size: 11px; color: var(--hs-inkfaint); }
.hs-sc { font-family: var(--hs-fnum); font-weight: 700; font-size: 13.5px; font-variant-numeric: tabular-nums; color: var(--hs-ink); }
.hs-sc .hs-st { color: var(--hs-gold); }
/* 人気ゲームの率を写す帯。回数を出さないので、大小はこの幅で見せる。
   .hs-nm は nowrap + ellipsis なので、帯は block にして名前の下へ落とす。
   ⚠️ この CSS はテンプレートリテラルの中。**バッククォートを書くとそこで文字列が切れる**
      （書いた直後に踏んだ。エラーは遠くの行に出るので原因が見えにくい）。 */
.hs-bar { display: block; height: 4px; margin-top: 5px; border-radius: 999px; background: var(--hs-linesoft); overflow: hidden; }
.hs-bar > i { display: block; height: 100%; border-radius: 999px; background: var(--hs-accent); min-width: 2px; }
.hs-row.me { background: var(--hs-accentsf); }
.hs-row.me .hs-nm { color: var(--hs-accentdk); }
.hs-empty { list-style: none; padding: 22px 14px; text-align: center; font-size: 12.5px; color: var(--hs-inkfaint); }

.hs-foot { margin-top: 30px; padding-top: 16px; border-top: 1px solid var(--hs-line); display: flex; align-items: center; gap: 12px 20px; flex-wrap: wrap; }
.hs-cr { font-size: 11px; letter-spacing: .04em; color: var(--hs-inkfaint); font-family: var(--hs-fnum); margin-left: auto; }
.hs-legal { display: flex; align-items: center; gap: 18px; flex-wrap: wrap; font-size: 12.5px; }
.hs-env { font-size: 12.5px; color: var(--hs-inkfaint); }
.hs-legal a { color: var(--hs-inksoft); text-decoration: none; border-bottom: 1px solid transparent; }
.hs-legal a:hover { color: var(--hs-accentdk); border-bottom-color: currentColor; }
/* 「データを削除」は戻せない操作なので、リンクの見た目のまま色だけ警告色にする
   （ボタンにすると押しやすくなりすぎ、目立たなすぎると探せない、の間を取る）。 */
.hs-dangerlink { font: inherit; background: none; border: 0; padding: 0; cursor: pointer; color: #C0392B; border-bottom: 1px solid transparent; }
.hs-dangerlink:hover { border-bottom-color: currentColor; }
@media (prefers-color-scheme: dark) { .hs-dangerlink { color: #E8796B; } }
/* 端末の管理 / ログアウト。**削除ほど戻せなくはない**ので警告色にはせず、
   規約リンクと同じ見た目にそろえる（フッターは後始末の場所、という意味づけを保つ）。 */
.hs-footlink { font: inherit; background: none; border: 0; padding: 0; cursor: pointer; color: var(--hs-inksoft); border-bottom: 1px solid transparent; }
.hs-footlink:hover { color: var(--hs-accentdk); border-bottom-color: currentColor; }

.hs-flash {
  background: var(--hs-accentsf); color: var(--hs-accentdk); border: 1px solid color-mix(in srgb, var(--hs-accent) 35%, transparent);
  border-radius: 12px; padding: 12px 18px; margin-bottom: 14px; font-size: 13.5px; font-weight: 600;
}
`;
